import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import Link from '@mui/material/Link';
import { FormattedMessage } from 'react-intl';

const PRODUCTS_LICENSES_OVERVIEW_LINK = 'https://docs.katalon.com/docs/products-and-licenses/katalon-studio-enterprise-and-runtime-engine-licenses/license-overview';
const STUDIO_OVERVIEW_LINK = 'https://docs.katalon.com/docs/katalon-studio-enterprise/katalon-studio-overview';
const RUN_TIME_ENGINE_INTRODUCTION_LINK = 'https://docs.katalon.com/docs/katalon-runtime-engine/introduction-to-runtime-engine';
const TEST_OPS_OVERVIEW_LINK = 'https://docs.katalon.com/docs/katalon-testops/overview/testops-overview';
const TEST_CLOUD_OVERVIEW_LINK = 'https://docs.katalon.com/docs/katalon-testcloud/testcloud-overview';
const RECORDER_OVERVIEW_LINK = 'https://docs.katalon.com/docs/katalon-recorder/get-started/overview';

const ABOUT_PLATFORM_LINK = 'https://docs.katalon.com/docs';
const PLATFORM_PLANS_LINK = 'https://docs.katalon.com/docs/administer/katalon-platform-packages/katalon-platform-plans';
const ROLES_AND_PERMISSIONS_LINK = 'https://docs.katalon.com/docs/administer/administration-roles/administrative-roles-and-permissions';
const PLATFORM_GET_STARTED_LINK = 'https://docs.katalon.com/docs/get-started/get-started-with-the-katalon-platform';

const useStyles = makeStyles(theme => ({
  boxRow: {
    display: 'row',
    marginTop: theme.spacing(4),
  },
  boxColumn: {
    display: 'column',
    width: '50%',
  },
  boxFlex: {
    display: 'flex',
  },
  link: {
    cursor: 'pointer',
    fontWeight: 500,
    color: '#276EF1',
    fontSize: theme.spacing(2),
    width: 'fit-content',
  },
  subTitle: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.spacing(2.5),
    marginBottom: theme.spacing(2),
    color: '#233145',
  },
  titleLink: {
    paddingBottom: theme.spacing(2),
  },
}));

interface FeaturedDocumentationProps {
  hasPlatformSubscription: boolean;
}

const FeaturedDocumentation = (props: FeaturedDocumentationProps) => {
  const classes = useStyles();
  const { hasPlatformSubscription } = props;

  const legacyDocumentation = (
    <Box className={classes.boxRow} justifyContent="space-between">
      <Typography variant="subtitle2" className={classes.subTitle}>
        <FormattedMessage id="admin.home.title.featured.documentation" />
      </Typography>
      <Box className={classes.boxFlex}>
        <Box className={classes.boxColumn} justifyContent="space-between">
          <Typography variant="body2" className={classes.titleLink}>
            <Link
              id="admin.home.link.products.and.licenses"
              className={classes.link}
              href={PRODUCTS_LICENSES_OVERVIEW_LINK}
              underline="none"
            >
              <FormattedMessage id="admin.home.link.products.and.licenses" />
            </Link>
          </Typography>
          <Typography variant="body2" className={classes.titleLink}>
            <Link
              id="admin.home.link.studio.overview"
              className={classes.link}
              href={STUDIO_OVERVIEW_LINK}
              underline="none"
            >
              <FormattedMessage id="admin.home.link.studio.overview" />
            </Link>
          </Typography>
          <Typography variant="body2" className={classes.titleLink}>
            <Link
              id="admin.home.link.runtime.engine.introduction"
              className={classes.link}
              href={RUN_TIME_ENGINE_INTRODUCTION_LINK}
              underline="none"
            >
              <FormattedMessage id="admin.home.link.runtime.engine.introduction" />
            </Link>
          </Typography>
        </Box>
        <Box className={classes.boxColumn} justifyContent="space-between">
          <Typography variant="body2" className={classes.titleLink}>
            <Link
              id="admin.home.link.test.ops.overview"
              className={classes.link}
              href={TEST_OPS_OVERVIEW_LINK}
              underline="none"
            >
              <FormattedMessage id="admin.home.link.test.ops.overview" />
            </Link>
          </Typography>
          <Typography variant="body2" className={classes.titleLink}>
            <Link
              id="admin.home.link.test.cloud.overview"
              className={classes.link}
              href={TEST_CLOUD_OVERVIEW_LINK}
              underline="none"
            >
              <FormattedMessage id="admin.home.link.test.cloud.overview" />
            </Link>
          </Typography>
          <Typography variant="body2" className={classes.titleLink}>
            <Link
              id="admin.home.link.recorder.overview"
              className={classes.link}
              href={RECORDER_OVERVIEW_LINK}
              underline="none"
            >
              <FormattedMessage id="admin.home.link.recorder.overview" />
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const platformDocumentation = (
    <Box className={classes.boxRow} justifyContent="space-between">
      <Typography variant="subtitle2" className={classes.subTitle}>
        <FormattedMessage id="admin.home.title.featured.documentation" />
      </Typography>
      <Box justifyContent="space-between" display="flex" flexWrap="wrap">
        <Box className={classes.boxColumn} justifyContent="space-between">
          <Typography variant="body2" className={classes.titleLink}>
            <Link
              id="admin.home.link.about_platform"
              className={classes.link}
              href={ABOUT_PLATFORM_LINK}
              underline="none"
            >
              <FormattedMessage id="admin.home.link.about_platform" />
            </Link>
          </Typography>
          <Typography variant="body2" className={classes.titleLink}>
            <Link
              id="admin.home.link.roles_and_permissions"
              className={classes.link}
              href={ROLES_AND_PERMISSIONS_LINK}
              underline="none"
            >
              <FormattedMessage id="admin.home.link.roles_and_permissions" />
            </Link>
          </Typography>
        </Box>
        <Box className={classes.boxColumn} justifyContent="space-between">
          <Typography variant="body2" className={classes.titleLink}>
            <Link
              id="admin.home.link.platform_plans"
              className={classes.link}
              href={PLATFORM_PLANS_LINK}
              underline="none"
            >
              <FormattedMessage id="admin.home.link.platform_plans" />
            </Link>
          </Typography>
          <Typography variant="body2" className={classes.titleLink}>
            <Link
              id="admin.home.link.platform_get_started"
              className={classes.link}
              href={PLATFORM_GET_STARTED_LINK}
              underline="none"
            >
              <FormattedMessage id="admin.home.link.platform_get_started" />
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return hasPlatformSubscription ? (platformDocumentation) : (legacyDocumentation);
};

export default FeaturedDocumentation;
